'use client';

import {
  AttributeSide,
  AttributeSideDynamic,
} from '@eliadmin/core/src/components/categories/attributes/attribute';
import { AttributeCategory } from '@eliadmin/core/src/components/categories/attributes/attribute-category';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card } from '@eliadmin/core/src/components/ui/card';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

import { LuX } from 'react-icons/lu';
import { TbEraser } from 'react-icons/tb';

const FilterSeparator = () => (
  <Separator className="h-[1px] w-full bg-[#EDEDED] last-of-type:hidden" />
);

export function CategoryFilterSheet() {
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const categoryTree = useCategoryStore(state => state.categoryTree);
  const category = useCategoryStore(state => state.category);
  const { t } = useTranslations();
  const clearFilter = useCategoryStore(s => s.clearFilters);

  const attributes = category.attributes?.filter(
    attribute => attribute.name && attribute.values?.length,
  );

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant={'light-white'}
          className="h-14 w-full bg-[#FAFAFA] text-black hover:bg-[#F0F0F0] sm:w-fit"
        >
          <Image
            src={'/images/icons/setting.svg'}
            alt=""
            width={20}
            height={20}
            className="object-contain"
          />
          {t('text174')}
        </Button>
      </SheetTrigger>
      <SheetContent
        aria-describedby={undefined}
        className="w-full max-w-[494px] rounded-l-[24px] border-none px-0 py-0 shadow-none sm:max-w-[494px]"
        showCloseIcon={false}
      >
        <Card
          className={cn(
            'relative flex h-screen flex-col bg-background',
            'ea-category-filter-sheet',
          )}
        >
          <div
            className={cn(
              'flex w-full flex-row items-start justify-between p-6',
              'ea-category-filter-sheet__expand-button',
            )}
          >
            <SheetTitle className="ea-heading__title h4 font-semibold tracking-tight text-black">
              {t('text174')}
            </SheetTitle>
            <SheetClose aria-label="Close">
              <LuX aria-hidden="true" className="text-[#969696]" size={24} />
            </SheetClose>
          </div>

          <div
            className={cn(
              'flex flex-1 flex-col gap-4 overflow-auto px-6 pb-4',
              'ea-category-filter-sheet__content',
            )}
          >
            <FilterSeparator />

            {!!templateSettings.category?.price_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text340')}
                  type="price"
                  attribute={{ type: 'status', type2: 'statuses' }}
                  order={templateSettings.category.price_in_filter.order}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__price') }}
                />
                <FilterSeparator />
              </>
            )}

            {!!templateSettings.category?.tree_list?.enabled && !!categoryTree && (
              <>
                <AttributeCategory order={templateSettings.category.tree_list.order} />
                <FilterSeparator />
              </>
            )}

            {!!templateSettings.category?.categories_in_filter?.enabled &&
              !!category.sub_categories?.length && (
                <>
                  <AttributeSide
                    name={t('text337')}
                    type="subCategory"
                    attribute={{ type: 'status', type2: 'statuses' }}
                    order={templateSettings.category.categories_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__subcategories') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {!!templateSettings.category?.manufacturers_in_filter?.enabled &&
              !!category.manufacturers?.length && (
                <>
                  <AttributeSide
                    name={t('text195')}
                    type="generic"
                    attribute={{ type: 'manufacturer', type2: 'manufacturers' }}
                    order={templateSettings.category.manufacturers_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__manufacturers') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {!!templateSettings.category?.statuses_in_filter?.enabled &&
              !!category.statuses?.length && (
                <>
                  <AttributeSide
                    name={t('text518')}
                    type="generic"
                    attribute={{ type: 'status', type2: 'statuses' }}
                    order={templateSettings.category?.categories_in_filter?.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__statuses') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {!!templateSettings.category?.availability_in_filter?.enabled &&
              !!category.availabilities?.length && (
                <>
                  <AttributeSide
                    name={t('text339')}
                    type="generic"
                    attribute={{ type: 'availability', type2: 'availabilities' }}
                    order={templateSettings.category.availability_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__availabilities') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {!!templateSettings.category?.attributes_in_filter?.enabled && (
              <>
                {attributes?.map((attribute, i) => (
                  <>
                    <AttributeSideDynamic
                      key={`attribute-${attribute.id ?? attribute.name}-${i}`}
                      name={attribute.name}
                      attribute={attribute as any}
                      className={{ wrapper: cn('', 'ea-category-filter-sheet__attributes') }}
                    />
                    <FilterSeparator />
                  </>
                ))}
              </>
            )}

            {!!templateSettings.category?.suppliers_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text718')}
                  type="generic"
                  attribute={{ type: 'supplier', type2: 'suppliers' }}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers') }}
                  order={templateSettings.category.suppliers_in_filter.order}
                />
                <FilterSeparator />
              </>
            )}

            {!!templateSettings.category?.suppliers_location_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text728')}
                  type="supplierLocation"
                  attribute={{ type: 'supplier', type2: 'suppliers' }}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers_location') }}
                  order={templateSettings.category?.suppliers_in_filter?.order}
                />
                <FilterSeparator />
              </>
            )}
          </div>

          <div
            className={cn(
              'sticky bottom-0 z-[20] mt-auto flex h-[80px] max-h-[80px] flex-col justify-end gap-2 bg-white px-6 py-4',
              'ea-category-filter-sheet__actions',
            )}
          >
            <div className="grid w-full grid-cols-2 items-center justify-center gap-2">
              <SheetClose asChild>
                <Button onClick={clearFilter} variant={'gray'} className="col-span-1 w-full">
                  <TbEraser size={24} /> {t('text632')}
                </Button>
              </SheetClose>
              <SheetClose asChild>
                <Button className="col-span-1 w-full">{t('text174')}</Button>
              </SheetClose>
            </div>
          </div>
        </Card>
      </SheetContent>
    </Sheet>
  );
}
